import React, { useState, useEffect } from "react";
import { X, Save, AlertCircle } from "lucide-react";

const EnquiryStageEditModal = ({
  isOpen,
  onClose,
  item,
  onSave,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    remarks: "",
    products: [],
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && item) {
      // Initialize form with item data
      setFormData({
        remarks: item.remarks || "",
        products: (item.productHistories || []).map((ph) => ({
          id: ph.id,
          productId: ph.product?.id,
          productName: ph.product?.name || "Unknown Product",
          quantity: ph.quantity || 0,
          price: ph.price || 0,
          categoryId: ph.product?.categoryId || null,
          specifications: (ph.specificationHistories || []).map((sh) => ({
            productSpecificationId: sh.productSpecification?.id || null,
            productSpecificationValueId:
              sh.productSpecificationValue?.id || null,
            specName: sh.productSpecification?.specName || "",
            value: sh.productSpecificationValue?.value || "",
            unit: sh.productSpecificationValue?.unit || "",
            topicDetails: sh.topicDetails || "",
          })),
        })),
      });
      setErrors({});
    }
  }, [isOpen, item]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    const newErrors = {};
    formData.products.forEach((product, idx) => {
      if (!product.quantity || product.quantity <= 0) {
        newErrors[`product-${idx}-quantity`] =
          "Quantity must be greater than 0";
      }
      if (product.price < 0) {
        newErrors[`product-${idx}-price`] = "Price cannot be negative";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Transform data for API
    const payload = {
      remarks: formData.remarks,
      products: formData.products.map((p) => ({
        productHistoryId: p.id,
        quantity: Number(p.quantity),
        price: Number(p.price),
        specificationHistories: p.specifications.map((s) => ({
          productSpecificationId: s.productSpecificationId,
          productSpecificationValueId: s.productSpecificationValueId,
          topicDetails: s.topicDetails || "",
        })),
      })),
    };

    const success = await onSave(payload);
    if (success) {
      onClose();
    }
  };

  const updateProduct = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) =>
        i === index ? { ...p, [field]: value } : p
      ),
    }));
    // Clear error for this field
    if (errors[`product-${index}-${field}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[`product-${index}-${field}`];
        return newErrors;
      });
    }
  };

  const updateSpecification = (productIndex, specIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) =>
        i === productIndex
          ? {
              ...p,
              specifications: p.specifications.map((s, j) =>
                j === specIndex ? { ...s, [field]: value } : s
              ),
            }
          : p
      ),
    }));
  };

  const addSpecification = (productIndex) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) =>
        i === productIndex
          ? {
              ...p,
              specifications: [
                ...p.specifications,
                {
                  productSpecificationId: null,
                  productSpecificationValueId: null,
                  specName: "",
                  value: "",
                  unit: "",
                  topicDetails: "",
                },
              ],
            }
          : p
      ),
    }));
  };

  const removeSpecification = (productIndex, specIndex) => {
    setFormData((prev) => ({
      ...prev,
      products: prev.products.map((p, i) =>
        i === productIndex
          ? {
              ...p,
              specifications: p.specifications.filter(
                (_, j) => j !== specIndex
              ),
            }
          : p
      ),
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Edit Enquiry Stage</h2>
            <p className="modal-subtitle">{item?.stageName}</p>
          </div>
          <button onClick={onClose} className="close-btn" type="button">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Remarks Section */}
            <div className="form-section">
              <label className="form-label">Remarks</label>
              <textarea
                value={formData.remarks}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, remarks: e.target.value }))
                }
                rows={3}
                placeholder="Add any remarks or notes..."
                className="form-textarea"
              />
            </div>

            {/* Products Section */}
            <div className="form-section">
              <h3 className="section-title">Products</h3>
              {formData.products.length === 0 ? (
                <div className="empty-state-small">
                  <AlertCircle size={24} />
                  <p>No products in this enquiry stage</p>
                </div>
              ) : (
                <div className="products-list">
                  {formData.products.map((product, productIndex) => (
                    <div key={product.id} className="product-edit-card">
                      <div className="product-edit-header">
                        <div className="product-name-badge">
                          <span className="product-number">
                            #{productIndex + 1}
                          </span>
                          <span className="product-name">
                            {product.productName}
                          </span>
                        </div>
                      </div>

                      <div className="form-grid-2">
                        <div className="form-group">
                          <label>Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            value={product.quantity}
                            onChange={(e) =>
                              updateProduct(
                                productIndex,
                                "quantity",
                                e.target.value
                              )
                            }
                            className={
                              errors[`product-${productIndex}-quantity`]
                                ? "error"
                                : ""
                            }
                          />
                          {errors[`product-${productIndex}-quantity`] && (
                            <span className="error-text">
                              {errors[`product-${productIndex}-quantity`]}
                            </span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Price (₹)</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={product.price}
                            onChange={(e) =>
                              updateProduct(
                                productIndex,
                                "price",
                                e.target.value
                              )
                            }
                            className={
                              errors[`product-${productIndex}-price`]
                                ? "error"
                                : ""
                            }
                          />
                          {errors[`product-${productIndex}-price`] && (
                            <span className="error-text">
                              {errors[`product-${productIndex}-price`]}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Specifications */}
                      <div className="specifications-section">
                        <div className="spec-header">
                          <label>Specifications</label>
                          <button
                            type="button"
                            onClick={() => addSpecification(productIndex)}
                            className="btn-add-spec"
                          >
                            + Add Spec
                          </button>
                        </div>

                        {product.specifications.map((spec, specIndex) => (
                          <div key={specIndex} className="spec-edit-item">
                            <div className="spec-info">
                              <span className="spec-name">
                                {spec.specName || "Specification"}
                              </span>
                              <span className="spec-value">
                                {spec.value} {spec.unit}
                              </span>
                            </div>

                            <div className="form-group">
                              <label>Topic Details</label>
                              <input
                                type="text"
                                value={spec.topicDetails}
                                onChange={(e) =>
                                  updateSpecification(
                                    productIndex,
                                    specIndex,
                                    "topicDetails",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter specification details..."
                              />
                            </div>

                            <button
                              type="button"
                              onClick={() =>
                                removeSpecification(productIndex, specIndex)
                              }
                              className="btn-remove-spec"
                              title="Remove specification"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}

                        {product.specifications.length === 0 && (
                          <p className="help-text">
                            No specifications added yet
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn-cancel"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save"
              disabled={loading || formData.products.length === 0}
            >
              <Save size={16} />
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
          overflow-y: auto;
        }

        .modal-content {
          background: white;
          border-radius: 12px;
          width: 100%;
          max-width: 800px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: #111827;
        }

        .modal-subtitle {
          margin: 0.25rem 0 0 0;
          font-size: 0.875rem;
          color: #6b7280;
        }

        .close-btn {
          background: none;
          border: none;
          color: #6b7280;
          cursor: pointer;
          padding: 0.5rem;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .form-section {
          margin-bottom: 2rem;
        }

        .form-section:last-child {
          margin-bottom: 0;
        }

        .section-title {
          font-size: 1.125rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 1rem 0;
        }

        .form-label {
          display: block;
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          resize: vertical;
          transition: all 0.2s;
        }

        .form-textarea:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .products-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .product-edit-card {
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 1rem;
          background: #f9fafb;
        }

        .product-edit-header {
          margin-bottom: 1rem;
        }

        .product-name-badge {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .product-number {
          background: #3b82f6;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .product-name {
          font-weight: 600;
          color: #111827;
        }

        .form-grid-2 {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 0.5rem;
        }

        .form-group input {
          padding: 0.625rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          transition: all 0.2s;
        }

        .form-group input:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
        }

        .form-group input.error {
          border-color: #ef4444;
        }

        .error-text {
          color: #ef4444;
          font-size: 0.75rem;
          margin-top: 0.25rem;
        }

        .specifications-section {
          background: white;
          padding: 1rem;
          border-radius: 6px;
          margin-top: 1rem;
        }

        .spec-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .spec-header label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
        }

        .btn-add-spec {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-add-spec:hover {
          background: #059669;
        }

        .spec-edit-item {
          background: #f9fafb;
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 0.75rem;
          position: relative;
        }

        .spec-edit-item:last-child {
          margin-bottom: 0;
        }

        .spec-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.75rem;
          padding-bottom: 0.75rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .spec-name {
          font-weight: 600;
          color: #111827;
          font-size: 0.875rem;
        }

        .spec-value {
          color: #6b7280;
          font-size: 0.875rem;
        }

        .btn-remove-spec {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: #fee;
          color: #dc2626;
          border: none;
          padding: 0.375rem;
          border-radius: 4px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .btn-remove-spec:hover {
          background: #fecaca;
        }

        .help-text {
          color: #6b7280;
          font-size: 0.875rem;
          font-style: italic;
          margin: 0;
        }

        .empty-state-small {
          text-align: center;
          padding: 2rem;
          color: #6b7280;
        }

        .empty-state-small svg {
          margin: 0 auto 0.5rem;
        }

        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1.5rem;
          border-top: 1px solid #e5e7eb;
        }

        .btn-cancel,
        .btn-save {
          padding: 0.625rem 1.25rem;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-cancel {
          background: #f3f4f6;
          color: #374151;
        }

        .btn-cancel:hover:not(:disabled) {
          background: #e5e7eb;
        }

        .btn-save {
          background: #3b82f6;
          color: white;
        }

        .btn-save:hover:not(:disabled) {
          background: #2563eb;
        }

        .btn-cancel:disabled,
        .btn-save:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .form-grid-2 {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default EnquiryStageEditModal;
